import type { ReactNode } from 'react';

export function Card({
  title,
  subtitle,
  actions,
  children,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card-head">
          <div>
            {title && <h2>{title}</h2>}
            {subtitle && <div className="sub">{subtitle}</div>}
          </div>
          {actions && <div className="toolbar">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function StatTile({
  label,
  value,
  foot,
}: {
  label: string;
  value: ReactNode;
  foot?: ReactNode;
}) {
  return (
    <div className="card tile">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {foot && <div className="foot">{foot}</div>}
    </div>
  );
}

export type BadgeTone = 'good' | 'warning' | 'serious' | 'critical' | 'muted' | 'default';

/**
 * Status is never carried by color alone - every badge ships a text label, and
 * the severe tones add a glyph, so the meaning survives colorblindness, print
 * and forced-colors mode.
 */
export function Badge({ tone = 'default', children }: { tone?: BadgeTone; children: ReactNode }) {
  const glyph =
    tone === 'critical' ? '\u25B2 ' : tone === 'warning' ? '\u25B3 ' : tone === 'good' ? '\u2713 ' : '';
  return (
    <span className={`badge ${tone === 'default' ? '' : tone}`}>
      {glyph}
      {children}
    </span>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <strong>{title}</strong>
      {children}
    </div>
  );
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'good' | 'warn' | 'danger';
  children: ReactNode;
}) {
  return <div className={`notice ${tone === 'info' ? '' : tone}`}>{children}</div>;
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="empty" role="status" aria-live="polite">
      {label}...
    </div>
  );
}
